import {
	DndContext,
	DragEndEvent,
	DragOverlay,
	DragStartEvent,
	DraggableAttributes,
	KeyboardSensor,
	Modifier,
	MouseSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	restrictToHorizontalAxis as restrictToHorizontalAxisModifier,
	restrictToParentElement as restrictToParentElementModifier,
	restrictToVerticalAxis as restrictToVerticalAxisModifier,
} from "@dnd-kit/modifiers";
import {
	SortableContext,
	horizontalListSortingStrategy,
	rectSortingStrategy,
	rectSwappingStrategy,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { Activity, useState } from "react";

import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";

const STRATEGY_MAP = {
	verticalList: verticalListSortingStrategy,
	rectSorting: rectSortingStrategy,
	rectSwapping: rectSwappingStrategy,
	horizontalList: horizontalListSortingStrategy,
};

type SortableItem<T> = T & {
	_id: string;
};

export type DragHandlers = {
	attributes: DraggableAttributes;
	listeners: SyntheticListenerMap | undefined;
};

export type SortableRenderProps = {
	isDragging: boolean;
	dragHandlers: DragHandlers;
	isDisabled: boolean;
};

export type SortableDragNDropProps<T> = {
	ref?: React.Ref<HTMLUListElement | HTMLOListElement>;
	/**
	 * Sorting strategy to use for the drag and drop.
	 */
	strategy?: keyof typeof STRATEGY_MAP;
	/**
	 * Array of items to display in the drag and drop list, requires each item to have a _id field.
	 */
	items: SortableItem<T>[];
	/**
	 * Custom render function for displaying items.
	 * @param item - The item to render
	 * @param sortableProps - The sortable props, which are a subset of the props result from the useSortable hook.
	 * @returns The rendered item
	 */
	renderItem: (item: SortableItem<T>, sortableProps: SortableRenderProps) => React.ReactNode;

	/**
	 * Whether to restrict the dragging to the parent element.
	 */
	restrictToParentElement?: boolean;
	/**
	 * Whether to restrict the dragging to the vertical axis.
	 */
	restrictToVerticalAxis?: boolean;
	/**
	 * Whether to restrict the dragging to the horizontal axis.
	 */
	restrictToHorizontalAxis?: boolean;
	/**
	 * Whether to enable auto-scroll when the dragging is near the edges of the list.
	 */
	autoScroll?: boolean;
	/**
	 * Whether to use an unordered list instead of an ordered list. (pure html semantic)
	 */
	useUnorderedList?: boolean;
	/**
	 * Optional CSS class name for the list container.
	 */
	listClassName?: string;
	/**
	 * Callback function called when the items are reordered.
	 * Receives the reordered array of items.
	 *
	 * **Note:** Either `onChange` or `onDragEnd` must be provided, but not both.
	 * Use `onChange` for simple state updates, or `onDragEnd` for more control
	 * (e.g., when using form libraries with `shiftValue` methods).
	 *
	 * @param items - The new array of items in their current order
	 */
	onChange?: (items: SortableItem<T>[]) => void;
	/**
	 * Callback function called when drag ends, providing the raw DragEndEvent.
	 * This allows you to handle reordering manually (e.g., using `field.shiftValue`).
	 *
	 * **Note:** Either `onChange` or `onDragEnd` must be provided, but not both.
	 * When `onDragEnd` is provided, internal reordering is skipped.
	 *
	 * @param event - The drag end event from @dnd-kit
	 */
	onDragEnd?: (event: DragEndEvent) => void;
	/**
	 * Callback function called when drag starts, providing the raw DragStartEvent.
	 * Useful for tracking which item is being dragged.
	 *
	 * @param event - The drag start event from @dnd-kit
	 */
	onDragStart?: (event: DragStartEvent) => void;
	/**
	 * Whether to show the drag overlay.
	 * @default true
	 */
	showDragOverlay?: boolean;
	/**
	 * Whether the drag and drop is disabled.
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * Optional aria-label for the list container.
	 */
	listAriaLabel?: string;
	/**
	 * Test identifier for the list container (used for testing).
	 */
	"data-testid"?: string;
};

const SortableItemWrapper = <T,>({
	item,
	renderItem,
	isDisabled,
}: {
	item: SortableItem<T>;
	renderItem: (item: SortableItem<T>, sortableProps: SortableRenderProps) => React.ReactNode;
	isDisabled: boolean;
}) => {
	const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({
		id: item._id,
		disabled: isDisabled,
	});

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		listStyle: "none",
	};

	const dragHandlers: DragHandlers = {
		attributes,
		listeners,
	};

	return (
		<li ref={setNodeRef} style={style} key={item._id}>
			{renderItem(item, { isDragging, dragHandlers, isDisabled })}
		</li>
	);
};

const SortableDragNDrop = <T,>({
	strategy = "verticalList",
	items,
	renderItem,
	restrictToParentElement = false,
	restrictToVerticalAxis = false,
	restrictToHorizontalAxis = false,
	autoScroll = false,
	useUnorderedList = false,
	listClassName,
	onChange,
	onDragEnd,
	onDragStart,
	showDragOverlay = false,
	disabled = false,
	listAriaLabel,
	"data-testid": dataTestId,
	ref,
}: SortableDragNDropProps<T>) => {
	const [activeItem, setActiveItem] = useState<SortableItem<T> | null>(null);

	const handleDragStart = (event: DragStartEvent) => {
		if (disabled) return;
		const { active } = event;
		setActiveItem(items.find((item) => item._id === active.id) || null);

		// Call user-provided onDragStart if provided
		if (onDragStart) {
			onDragStart(event);
		}
	};

	const sensors = useSensors(
		useSensor(MouseSensor, {
			activationConstraint: {
				distance: 10, // Enable sort function when dragging 10px
			},
		}),
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const modifiers: Modifier[] = [];
	if (restrictToParentElement) {
		modifiers.push(restrictToParentElementModifier);
	}

	if (restrictToVerticalAxis) {
		modifiers.push(restrictToVerticalAxisModifier);
	}

	if (restrictToHorizontalAxis) {
		modifiers.push(restrictToHorizontalAxisModifier);
	}

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setActiveItem(null);

		if (disabled) return;

		// If onDragEnd callback is provided, call it and let consumer handle everything
		if (onDragEnd) {
			onDragEnd(event);
			return;
		}

		// Otherwise, handle reordering internally and call onChange
		if (over && active.id !== over.id && onChange) {
			const oldIndex = items.findIndex((item) => item._id === active.id);
			const newIndex = items.findIndex((item) => item._id === over.id);

			if (oldIndex !== -1 && newIndex !== -1) {
				const newItems = [...items];
				const [movedItem] = newItems.splice(oldIndex, 1);
				newItems.splice(newIndex, 0, movedItem);
				onChange(newItems);
			}
		}
	};

	const ListElement = useUnorderedList ? "ul" : "ol";

	return (
		<DndContext
			collisionDetection={closestCenter}
			sensors={disabled ? [] : sensors}
			autoScroll={disabled ? false : autoScroll}
			modifiers={modifiers}
			onDragEnd={handleDragEnd}
			onDragStart={handleDragStart}
		>
			<SortableContext items={items.map((item) => item._id)} strategy={STRATEGY_MAP[strategy]}>
				<ListElement
					ref={ref as React.Ref<HTMLUListElement & HTMLOListElement>}
					className={listClassName}
					style={{ margin: 0, padding: 0, listStyle: "none" }}
					aria-label={listAriaLabel}
					data-testid={dataTestId}
				>
					{items.map((item) => (
						<SortableItemWrapper key={item._id} item={item} renderItem={renderItem} isDisabled={disabled} />
					))}
				</ListElement>
			</SortableContext>
			<Activity mode={showDragOverlay && !disabled ? "visible" : "hidden"}>
				<DragOverlay>
					{activeItem ? (
						<SortableItemWrapper item={activeItem} renderItem={renderItem} isDisabled={disabled} />
					) : null}
				</DragOverlay>
			</Activity>
		</DndContext>
	);
};

export default SortableDragNDrop;
